import { z } from 'zod';
import { fetch } from '@/modules/fetch';
import {
  reportGatewayStatus,
  type DownBackend,
} from '@/modules/backend-down-signal';
import { reportSchemaMismatch } from '@/modules/report-schema-mismatch';
import { readJsonBody } from '@/modules/read-json-body';

// The shared transport entry for the SanR-owned API clients: the response's
// status is fed to @/modules/backend-down-signal BEFORE the response returns
// to the caller — gateway-outage detection never depends on how a caller
// consumes the response or its rejection.
export async function gatewayFetch(
  backend: DownBackend,
  input: string,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetch(input, init);
  reportGatewayStatus(backend, res.status);
  return res;
}

// Thrown for every non-ok response of the REST clients. `body` is the parsed
// response body (undefined when empty / non-JSON); `extra` carries the
// request context — endpointId, method, url, status, responseBody,
// requestBody (the v1 client adds queryParams) — shaped for lifting into
// error-report contexts. A gateway status (502/503) in `extra.status` is
// what the outage-aware error toast keys on, so every client throws this
// class, never a plain Error.
export class FetchApiError extends Error {
  override name = 'FetchApiError';
  readonly body: unknown;
  readonly extra: Record<string, unknown>;
  constructor(message: string, body: unknown, extra: Record<string, unknown>) {
    super(message);
    this.body = body;
    this.extra = extra;
  }
}

type MismatchSource = Parameters<typeof reportSchemaMismatch>[0];

type FetchJsonOptions<S extends z.ZodType> = {
  // null = a third-party origin whose downtime must never feed the app-wide
  // outage signal (the request then goes through the fetch seam directly).
  backend: DownBackend | null;
  source: MismatchSource;
  // The endpoint id (or path) tagging the error message and the drift
  // report.
  key: string;
  url: string;
  init?: RequestInit | undefined;
  schema: S;
  nullOn404?: boolean | undefined;
  // Echoed into FetchApiError.extra for error reports.
  requestBody?: unknown;
};

// The one JSON fetch-and-parse of the REST clients. A 404 resolves null
// under nullOn404 (the body unread); a non-ok status throws FetchApiError
// (`<METHOD> <key> → <status>`, body and context attached); an ok body
// parses fail-open through parseFailOpen, EXCEPT a body that is no JSON
// container at all (undefined, null, scalar) — every consumer would crash on
// property access, so after the drift report that one throws FetchApiError
// instead of returning a type lie.
export function fetchJson<S extends z.ZodType>(
  opts: FetchJsonOptions<S> & { nullOn404?: false | undefined },
): Promise<z.output<S>>;
export function fetchJson<S extends z.ZodType>(
  opts: FetchJsonOptions<S>,
): Promise<z.output<S> | null>;
export async function fetchJson<S extends z.ZodType>(
  opts: FetchJsonOptions<S>,
): Promise<z.output<S> | null> {
  const method = opts.init?.method ?? 'GET';
  const res =
    opts.backend === null ?
      await fetch(opts.url, opts.init)
    : await gatewayFetch(opts.backend, opts.url, opts.init);
  if (opts.nullOn404 === true && res.status === 404) return null;
  const body = await readJsonBody(res);
  const context = {
    endpointId: opts.key,
    method,
    url: opts.url,
    status: res.status,
    responseBody: body,
    requestBody: opts.requestBody,
  };
  if (!res.ok) {
    throw new FetchApiError(
      `${method} ${opts.key} → ${res.status}`,
      body,
      context,
    );
  }
  return parseFailOpen(opts.source, opts.key, opts.schema, body, (raw) => {
    if (typeof raw !== 'object' || raw === null) {
      throw new FetchApiError(
        `${method} ${opts.key} → ${res.status} non-container body`,
        raw,
        context,
      );
    }
  });
}

// Health probe for gateway-outage recovery polling: true = the origin
// answered the ping URL with an ok status. Never throws — network-phase
// failures and non-ok statuses both mean "still down" (false). cache:
// 'no-store' keeps every probe an end-to-end request — a cached 200 would
// fake a recovery. Deliberately outside gatewayFetch: a probe's status must
// not re-report the outage it is checking.
export async function pingOk(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, { cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
}

// Fail-open body parse: a schema mismatch reports through
// @/modules/report-schema-mismatch and the RAW body is returned — consumers
// never block on drift. `onDrift` (optional) runs after the report and may
// throw to fail CLOSED for bodies a caller cannot degrade through (e.g. a
// non-container body every consumer would crash on).
export function parseFailOpen<S extends z.ZodType>(
  source: MismatchSource,
  key: string,
  schema: S,
  raw: unknown,
  onDrift?: (raw: unknown) => void,
): z.output<S> {
  const parsed = schema.safeParse(raw);
  if (parsed.success) return parsed.data;
  reportSchemaMismatch(source, key, parsed.error, raw);
  onDrift?.(raw);
  return raw as z.output<S>;
}
